import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  ChevronRight,
  Mail,
  Forward,
  Sparkles,
  BookOpen,
  Sliders,
  AlertTriangle,
  Zap,
  Copy,
  Building2,
  ShieldOff,
  Archive,
  Plug,
  Bell,
  ScrollText,
} from "lucide-react";
import { Card, CardBody, CardHeader } from "../components/Card";
import { Button } from "../components/Button";
import { useMe } from "../lib/me";

const SECTIONS = [
  { id: "quick-start", label: "Quick start", icon: Zap },
  { id: "forwarding", label: "Forwarding setup", icon: Forward },
  { id: "sender-email", label: "Sender email", icon: Mail },
  { id: "categories", label: "Setting up categories", icon: Sparkles },
  { id: "knowledge", label: "Knowledge bank", icon: BookOpen },
  { id: "reply-modes", label: "Auto-replies vs review", icon: Sliders },
  { id: "business-context", label: "Business context", icon: Building2 },
  { id: "sender-filter", label: "Sender filter", icon: ShieldOff },
  { id: "outbound-archive", label: "Outbound archive", icon: Archive },
  { id: "connectors", label: "Connectors", icon: Plug },
  { id: "slack-digest", label: "Slack digest", icon: Bell },
  { id: "audit-log", label: "Audit log", icon: ScrollText },
  { id: "troubleshooting", label: "Troubleshooting", icon: AlertTriangle },
];

export default function Help() {
  const [active, setActive] = useState(SECTIONS[0]!.id);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setActive(e.target.id);
            break;
          }
        }
      },
      { rootMargin: "-30% 0px -60% 0px" },
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-[220px_1fr]">
      {/* Sidebar TOC */}
      <aside className="lg:sticky lg:top-24 lg:self-start">
        <h1 className="text-2xl font-semibold tracking-tight text-gray-900">Help & setup</h1>
        <p className="mt-1 text-sm text-gray-500">
          Everything you need to get Triage running with your existing support inbox.
        </p>
        <nav className="mt-6 space-y-1">
          {SECTIONS.map((s) => {
            const Icon = s.icon;
            return (
              <a
                key={s.id}
                href={`#${s.id}`}
                className={
                  "flex items-center gap-2 rounded-md px-2.5 py-1.5 text-sm transition-colors " +
                  (active === s.id
                    ? "bg-gray-900 text-white"
                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900")
                }
              >
                <Icon className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{s.label}</span>
              </a>
            );
          })}
        </nav>
      </aside>

      {/* Content */}
      <div className="min-w-0 space-y-12 pb-24">
        <QuickStart />
        <Forwarding />
        <SenderEmailHelp />
        <CategoriesHelp />
        <KnowledgeHelp />
        <ReplyModesHelp />
        <BusinessContextHelp />
        <SenderFilterHelp />
        <OutboundArchiveHelp />
        <ConnectorsHelp />
        <SlackDigestHelp />
        <AuditLogHelp />
        <Troubleshooting />
      </div>
    </div>
  );
}

/* ── Quick start ──────────────────────────────────────────────────────── */

function QuickStart() {
  const { me } = useMe();
  const inbound = me.tenant ? `support@${me.tenant.inboundSubdomain}` : "support@<your-slug>.inbox.appgambit.com";
  return (
    <SectionContainer id="quick-start" title="Quick start" icon={Zap}>
      <p>
        Three things get a fresh tenant working end-to-end:
      </p>
      <ol className="mt-4 list-inside list-decimal space-y-2 text-sm">
        <li>
          <strong>Send a test email</strong> to your inbound address (
          <CopyChip text={inbound} />) and confirm it lands in{" "}
          <Link to="/cases" className="font-medium text-gray-900 underline">Cases</Link>.
        </li>
        <li>
          <strong>Install or create at least one category</strong> in{" "}
          <Link to="/categories" className="font-medium text-gray-900 underline">Categories</Link>.
          Without categories, the AI has nothing to classify against.
        </li>
        <li>
          <strong>Approve your first AI reply</strong> from the{" "}
          <Link to="/review" className="font-medium text-gray-900 underline">Review queue</Link>{" "}
          to validate the loop.
        </li>
      </ol>
      <p className="mt-4 text-sm text-gray-600">
        After this, you typically want to wire up email forwarding from your real support inbox
        (e.g., <code className="rounded bg-gray-100 px-1 text-xs">support@yourcompany.com</code>)
        to Triage so customers never see the appgambit domain. See{" "}
        <a href="#forwarding" className="font-medium text-gray-900 underline">Forwarding setup</a>.
      </p>
      <p className="mt-2 text-sm text-gray-600">
        Once that's working, the highest-leverage additions are{" "}
        <a href="#business-context" className="font-medium text-gray-900 underline">Business context</a>{" "}
        (better grounding for every reply),{" "}
        <a href="#connectors" className="font-medium text-gray-900 underline">Connectors</a>{" "}
        (real-time data from your APIs), and the{" "}
        <a href="#slack-digest" className="font-medium text-gray-900 underline">Slack digest</a>{" "}
        (daily snapshot — know it's working without checking the dashboard).
      </p>
    </SectionContainer>
  );
}

/* ── Forwarding setup ─────────────────────────────────────────────────── */

function Forwarding() {
  const { me } = useMe();
  const inbound = me.tenant ? `support@${me.tenant.inboundSubdomain}` : "support@<your-slug>.inbox.appgambit.com";
  return (
    <SectionContainer id="forwarding" title="Email forwarding setup" icon={Forward}>
      <p>
        You probably want customers to keep emailing your existing support address
        (<code className="rounded bg-gray-100 px-1 text-xs">support@yourcompany.com</code>) instead
        of seeing the appgambit subdomain. Forwarding bridges the two: customers send to your
        inbox, your provider auto-forwards to Triage.
      </p>

      <Sub title="Step 1 — Verify your sender email">
        <p>
          Go to{" "}
          <Link to="/settings" className="font-medium text-gray-900 underline">Settings → Sender email</Link>{" "}
          and enter your support address (e.g. <code className="rounded bg-gray-100 px-1 text-xs">support@yourcompany.com</code>).
          AWS SES will email a verification link to that address. Click the link, and the badge
          flips to <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-xs font-medium text-emerald-700">Verified</span>.
        </p>
      </Sub>

      <Sub title="Step 2 — Switch reply addressing">
        <p>
          In{" "}
          <Link to="/settings" className="font-medium text-gray-900 underline">Settings → Reply addressing</Link>,
          choose <strong>"Use my verified sender"</strong>. From this point, both From and Reply-To on
          outbound replies will be your verified address — customers never see the appgambit domain.
        </p>
      </Sub>

      <Sub title="Step 3 — Forward your support inbox to Triage">
        <p>
          Set up a forwarding rule from{" "}
          <code className="rounded bg-gray-100 px-1 text-xs">support@yourcompany.com</code> to{" "}
          <CopyChip text={inbound} />. Pick the guide for your provider:
        </p>

        <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
          <p className="font-semibold">💡 Hybrid setup — keep Gmail access for off-category email</p>
          <p className="mt-1.5">
            When forwarding, choose <strong>"keep a copy in Inbox"</strong> in your provider's
            settings. That way every email goes to both Gmail (so you can read / reply manually
            for things the AI shouldn't handle) AND Triage (which will classify, draft, and
            either auto-reply or queue for your review). Set categories you trust to "Direct"
            mode in Triage and let the rest land in the Review queue.
          </p>
          <p className="mt-1.5">
            <strong>Caveat:</strong> if you also reply manually from Gmail, the customer may get
            two replies (yours + Triage's). Either keep Triage in <em>Draft</em> mode for
            those categories, or pick one channel for replies per category.
          </p>
        </div>

        <Provider title="Google Workspace — Gmail Settings (per-mailbox, simplest for a single inbox)">
          <p>
            For a single mailbox like{" "}
            <code className="rounded bg-gray-100 px-1 text-xs">hello@yourcompany.com</code>, use
            Gmail's per-account Forwarding setting. You don't need admin console access — just
            sign-in to that mailbox.
          </p>
          <ol className="mt-2 list-inside list-decimal space-y-1.5">
            <li>
              Open Gmail signed in as <strong>hello@yourcompany.com</strong> (or whichever inbox
              you're forwarding from).
            </li>
            <li>
              Click the gear icon (⚙️) in the top-right → <strong>See all settings</strong>.
            </li>
            <li>
              Open the <strong>Forwarding and POP/IMAP</strong> tab.
            </li>
            <li>
              Under <em>Forwarding</em>, click <strong>Add a forwarding address</strong>. Paste{" "}
              <CopyChip text={inbound} /> and click <strong>Next</strong> → <strong>Proceed</strong> →{" "}
              <strong>OK</strong>.
            </li>
            <li>
              Gmail sends a confirmation code to that address. <strong>Open Triage →{" "}
                <Link to="/cases" className="font-medium text-gray-900 underline">Cases</Link></strong>{" "}
              and find the verification email (subject usually starts with{" "}
              <em>"Gmail Forwarding Confirmation"</em>). Click the link inside the email body, or
              copy the 9-digit code and paste it back into Gmail's confirmation prompt.
            </li>
            <li>
              Back in Gmail Settings (you may need to refresh), the radio button{" "}
              <em>"Forward a copy of incoming mail to <strong>{inbound}</strong>"</em> is now
              available. Select it.
            </li>
            <li>
              <strong>Choose what Gmail does with its own copy</strong>:
              <ul className="ml-6 mt-1 list-disc space-y-0.5">
                <li>
                  <strong>"keep Gmail's copy in the Inbox"</strong> — recommended for hybrid setup.
                  You see every email in Gmail AND Triage processes them.
                </li>
                <li>
                  <em>"mark Gmail's copy as read"</em> — same as above but reduces unread badge
                  noise.
                </li>
                <li>
                  <em>"archive Gmail's copy"</em> / <em>"delete Gmail's copy"</em> — Triage-only,
                  Gmail acts as backup.
                </li>
              </ul>
            </li>
            <li>
              Scroll to the bottom of the Settings page and click{" "}
              <strong>Save Changes</strong>.
            </li>
          </ol>
          <p className="mt-2 text-xs text-gray-500">
            Test by sending an email from another address to the source inbox. Within ~30 seconds
            it should appear in Triage's <Link to="/cases" className="text-gray-900 underline">Cases</Link>{" "}
            and (if you chose "keep") in Gmail.
          </p>
        </Provider>

        <Provider title="Google Workspace — Admin Routing (org-wide, for aliases / multiple inboxes)">
          <p>
            Use this if you're a Workspace admin and want to forward at the org level — e.g.,
            you've got an alias <code className="rounded bg-gray-100 px-1 text-xs">support@</code>{" "}
            that distributes to several people, or you don't want to log into the mailbox.
          </p>
          <p className="mt-2 text-xs text-amber-700">
            Heads up: Google's admin UI has been reorganized several times. Current path (mid-2026)
            is below; if a label doesn't match, look for "Routing" or "Compliance" under Gmail.
          </p>
          <ol className="mt-2 list-inside list-decimal space-y-1.5">
            <li>
              Open <em>admin.google.com</em>. Sign in with a Workspace admin account.
            </li>
            <li>
              <strong>Apps</strong> → <strong>Google Workspace</strong> → <strong>Gmail</strong>.
            </li>
            <li>
              Scroll to <strong>Routing</strong> (sometimes labeled "Compliance" → "Routing"). Click
              into it.
            </li>
            <li>
              Make sure the org / OU at the top is the right scope (usually root org for forwarding).
            </li>
            <li>
              Click <strong>Add another rule</strong>. Name it "Triage forwarding".
            </li>
            <li>
              Under <em>"Email messages to affect"</em>, check <strong>Inbound</strong>.
            </li>
            <li>
              Under <em>"For the following types of messages"</em>, click <strong>Add</strong>.
              <ul className="ml-6 mt-1 list-disc">
                <li>Type of expression: <em>Advanced content match</em></li>
                <li>Location: <em>Envelope recipient</em></li>
                <li>Match type: <em>Matches</em> (or "Contains" with the full address)</li>
                <li>Content: <code className="rounded bg-gray-100 px-1 text-xs">hello@yourcompany.com</code></li>
              </ul>
            </li>
            <li>
              Under <em>"If the above expressions match, do the following"</em>, scroll to{" "}
              <strong>"Also deliver to"</strong> and add{" "}
              <code className="rounded bg-gray-100 px-1 text-xs">{inbound}</code>.
            </li>
            <li>
              <strong>Do NOT</strong> check "Account types to affect: Unrecognized / external" —
              leave defaults. Leave headers untouched (do not rewrite envelope sender).
            </li>
            <li>
              Save. Click <strong>Save</strong> on the rule, then <strong>Save</strong> at the
              bottom of the Routing screen. Propagation takes a few minutes.
            </li>
          </ol>
          <p className="mt-2 text-xs text-gray-500">
            For "keep a copy in Gmail" with this approach, the original delivery to the mailbox
            stays — Routing's <em>"Also deliver to"</em> adds Triage as an additional recipient
            without removing the original.
          </p>
        </Provider>

        <Provider title="Microsoft 365 (Exchange Online)">
          <ol className="list-inside list-decimal space-y-1.5">
            <li>
              Open the <em>Exchange admin center</em> → Recipients → Mailboxes →{" "}
              <strong>support@yourcompany.com</strong>.
            </li>
            <li>
              <strong>Mailbox features</strong> → <strong>Mail flow settings</strong> →{" "}
              <strong>Delivery options</strong>.
            </li>
            <li>
              Set <em>Forwarding address</em> to{" "}
              <code className="rounded bg-gray-100 px-1 text-xs">{inbound}</code>.
            </li>
            <li>
              Check <strong>"Deliver message to both forwarded address and mailbox"</strong> if
              you want a copy archived in your support inbox too.
            </li>
            <li>Save.</li>
          </ol>
          <p className="mt-2 text-xs text-gray-500">
            For finer control, use a <em>Mail flow rule</em> (Exchange transport rule) with a
            <em>Bcc</em> action to the inbound address — preserves headers cleanly.
          </p>
        </Provider>

        <Provider title="Postfix / custom SMTP server">
          <p>
            Add a virtual alias in <code className="rounded bg-gray-100 px-1 text-xs">/etc/postfix/virtual</code>:
          </p>
          <pre className="mt-2 overflow-x-auto rounded-md border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-xs">
            {`support@yourcompany.com  support@yourcompany.com,${inbound}`}
          </pre>
          <p className="mt-2">
            Then <code className="rounded bg-gray-100 px-1 text-xs">postmap /etc/postfix/virtual && systemctl reload postfix</code>.
            This delivers locally AND forwards to Triage. Headers preserved.
          </p>
        </Provider>

        <Provider title="Cloudflare Email Routing (free, popular for indie / bootstrapped)">
          <p>
            Best option if you own a domain on Cloudflare but don't run a mailbox there yet.
            Routes mail to any external address — no mailbox needed.
          </p>
          <ol className="list-inside list-decimal space-y-1.5">
            <li>Cloudflare dashboard → select your domain → <strong>Email</strong> → <strong>Email Routing</strong>.</li>
            <li>Click <strong>Get started</strong> if you haven't enabled it. Cloudflare adds the MX + TXT records automatically.</li>
            <li>Under <strong>Routing rules</strong>, click <strong>Create address</strong>.</li>
            <li>
              Custom address: <code className="rounded bg-gray-100 px-1 text-xs">support@yourcompany.com</code>.<br />
              Action: <em>Send to an email</em>.<br />
              Destination: <code className="rounded bg-gray-100 px-1 text-xs">{inbound}</code>.
            </li>
            <li>Save. Cloudflare emails the destination address with a one-time verification link — click it.</li>
            <li>Once verified, the rule is active immediately.</li>
          </ol>
          <p className="mt-2 text-xs text-gray-500">
            Cloudflare Email Routing is free up to 200 destination addresses. Headers are
            preserved.
          </p>
        </Provider>

        <Provider title="GoDaddy / cPanel-style hosting (Bluehost, HostGator, Namecheap Private Email, etc.)">
          <p>Most cPanel-based hosts have a "Forwarders" feature.</p>
          <ol className="list-inside list-decimal space-y-1.5">
            <li>Sign in to your hosting control panel (cPanel, Plesk, or the host's web admin).</li>
            <li>Find <strong>Email</strong> → <strong>Forwarders</strong> (sometimes labeled "Email Aliases" or "Mail Forwarding").</li>
            <li>Click <strong>Add Forwarder</strong>.</li>
            <li>
              Address to forward: <strong>support</strong> (the part before @ — your domain is auto-appended).<br />
              Forward to email address: <code className="rounded bg-gray-100 px-1 text-xs">{inbound}</code>.
            </li>
            <li>Save. Effective immediately, no verification step.</li>
          </ol>
          <p className="mt-2 text-xs text-gray-500">
            cPanel forwarders preserve headers fine. If you also want a copy in your existing
            mailbox, set it up as a "redirect" alongside the forward.
          </p>
        </Provider>

        <Provider title="Zoho Mail">
          <ol className="list-inside list-decimal space-y-1.5">
            <li>Sign in to <em>mail.zoho.com</em>.</li>
            <li>Settings (gear icon) → <strong>Mail Forwarding and POP/IMAP</strong>.</li>
            <li>Click <strong>Add Email Address</strong> under Forwarding.</li>
            <li>Enter <code className="rounded bg-gray-100 px-1 text-xs">{inbound}</code>.</li>
            <li>Zoho sends a confirmation code to that address. Triage receives it as a new case — find the code in Cases, paste it back into Zoho.</li>
            <li>Enable the forward + check "Keep a copy in inbox" if you want.</li>
          </ol>
          <p className="mt-2 text-xs text-gray-500">
            For Zoho admin-level forwards across the org, use <em>Mail Admin Console → Routing → Email Routing</em> to set up a server-level forward.
          </p>
        </Provider>

        <Provider title="Personal Gmail (gmail.com address — not Workspace)">
          <p>
            If your "support" address is just a personal Gmail you check, you can forward at the
            account level.
          </p>
          <ol className="list-inside list-decimal space-y-1.5">
            <li>Open Gmail → ⚙️ Settings → <strong>See all settings</strong> → <strong>Forwarding and POP/IMAP</strong>.</li>
            <li>Click <strong>Add a forwarding address</strong>. Enter <code className="rounded bg-gray-100 px-1 text-xs">{inbound}</code>.</li>
            <li>Gmail sends a verification email. Open <Link to="/cases" className="font-medium text-gray-900 underline">Cases</Link> in Triage — the verification email arrives as a new case. Find the confirmation code or click the verification link from there.</li>
            <li>Back in Gmail Settings, choose <em>Forward a copy of incoming mail to {inbound}</em> and what to do with the original (keep / archive).</li>
            <li>Save.</li>
          </ol>
          <p className="mt-2 text-xs text-gray-500">
            For more granular forwarding (e.g. only label "Support"), create a Gmail filter that
            forwards messages matching that label instead.
          </p>
        </Provider>

        <Provider title="Outlook.com / Hotmail (personal Microsoft account)">
          <ol className="list-inside list-decimal space-y-1.5">
            <li>Sign in to <em>outlook.live.com</em> → ⚙️ Settings → <strong>View all Outlook settings</strong> → <strong>Mail</strong> → <strong>Forwarding</strong>.</li>
            <li>Toggle <strong>Enable forwarding</strong>.</li>
            <li>Forward my email to: <code className="rounded bg-gray-100 px-1 text-xs">{inbound}</code>.</li>
            <li>Check <strong>"Keep a copy of forwarded messages"</strong> if you want a paper trail.</li>
            <li>Save.</li>
          </ol>
        </Provider>

        <Provider title="Apple iCloud Mail">
          <ol className="list-inside list-decimal space-y-1.5">
            <li>Sign in to <em>icloud.com</em> → Mail.</li>
            <li>Click ⚙️ at the bottom of the mailbox list → <strong>Preferences</strong>.</li>
            <li><strong>General</strong> tab → check <em>Forward my email to</em> → enter <code className="rounded bg-gray-100 px-1 text-xs">{inbound}</code>.</li>
            <li>Choose to delete or keep messages after forwarding.</li>
            <li>Save.</li>
          </ol>
        </Provider>

        <Provider title="Postfix / custom SMTP server">
          <p>
            Add a virtual alias in <code className="rounded bg-gray-100 px-1 text-xs">/etc/postfix/virtual</code>:
          </p>
          <pre className="mt-2 overflow-x-auto rounded-md border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-xs">
            {`support@yourcompany.com  support@yourcompany.com,${inbound}`}
          </pre>
          <p className="mt-2">
            Then run <code className="rounded bg-gray-100 px-1 text-xs">postmap /etc/postfix/virtual && systemctl reload postfix</code>.
            This delivers locally AND forwards to Triage. Headers preserved.
          </p>
        </Provider>

        <Provider title="Anything else (FastMail, ProtonMail, Mailbox.org, etc.)">
          <p>
            Look for a "Forwarding" or "Auto-forward" setting under your mailbox or admin panel.
            The three things to look for:
          </p>
          <ul className="mt-2 list-inside list-disc space-y-1">
            <li>Plain SMTP forward — not a "redirect" that rewrites the envelope sender.</li>
            <li>Preserves original <code className="rounded bg-gray-100 px-1 text-xs">From</code>, <code className="rounded bg-gray-100 px-1 text-xs">Message-ID</code>, and <code className="rounded bg-gray-100 px-1 text-xs">In-Reply-To</code> headers.</li>
            <li>No subject prefix injection (some forwarders add "Fwd:" — workable but ugly).</li>
          </ul>
          <p className="mt-2">
            If your provider isn't listed here and the forward works correctly when tested, email{" "}
            <a href="mailto:hello@appgambit.com" className="font-medium text-gray-900 underline">hello@appgambit.com</a>{" "}
            so we can add the steps to this guide.
          </p>
        </Provider>
      </Sub>

      <Sub title="Step 4 — Add SPF for deliverability">
        <p>
          When Triage sends a reply <em>from</em> your domain, the recipient's mail server checks
          your SPF record. If SES isn't included, replies may land in spam. Add this DNS TXT record
          on your sender domain:
        </p>
        <code className="mt-2 block break-all rounded-md border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-xs">
          v=spf1 include:amazonses.com -all
        </code>
        <p className="mt-2">
          If you already have an SPF record, merge:{" "}
          <code className="rounded bg-gray-100 px-1 text-xs">v=spf1 include:amazonses.com include:_spf.google.com -all</code>.
          Only one SPF record per domain — having two breaks both.
        </p>
      </Sub>

      <Sub title="Step 5 — Test it">
        <ol className="list-inside list-decimal space-y-1.5">
          <li>From a personal inbox, send an email to <code className="rounded bg-gray-100 px-1 text-xs">support@yourcompany.com</code>.</li>
          <li>Within 30 seconds, the case appears in <Link to="/cases" className="font-medium text-gray-900 underline">Cases</Link>, classified.</li>
          <li>Either approve the AI draft (Review queue) or wait for auto-send if you've enabled it.</li>
          <li>The reply arrives at your personal inbox — <strong>From: support@yourcompany.com</strong>, no appgambit anywhere.</li>
          <li>Hit reply on that email. Your reply forwards back to Triage and threads onto the same case.</li>
        </ol>
      </Sub>
    </SectionContainer>
  );
}

/* ── Sender email ─────────────────────────────────────────────────────── */

function SenderEmailHelp() {
  return (
    <SectionContainer id="sender-email" title="Sender email verification" icon={Mail}>
      <p>
        AWS SES requires you to verify ownership of any email address it sends from. Triage uses
        SES email-level identity verification — you enter an address, click a link in the
        verification email, done.
      </p>
      <Sub title="What to do">
        <ol className="list-inside list-decimal space-y-1.5">
          <li>
            <Link to="/settings" className="font-medium text-gray-900 underline">Settings → Sender email</Link>.
          </li>
          <li>Enter the address you want replies to come from.</li>
          <li>SES emails a verification link to that address (within ~1 minute). Check spam.</li>
          <li>Click the link. The Settings page polls every 5 seconds and flips the badge to <strong>Verified</strong>.</li>
        </ol>
      </Sub>
      <Sub title="Common issues">
        <ul className="list-inside list-disc space-y-1.5">
          <li><strong>No verification email arrives within 5 minutes:</strong> click <em>Resend</em> in Settings. SES sometimes drops the first one.</li>
          <li><strong>Click the link but status stays Pending:</strong> click <em>Check</em> in Settings to force a re-fetch from SES.</li>
          <li><strong>Need a different address:</strong> click <em>Remove</em>, then add the new one (this deletes the old SES identity).</li>
        </ul>
      </Sub>
      <Sub title="Domain-level verification (coming in v1.1)">
        <p>
          For best deliverability — DKIM-signed mail aligned with your domain, not Triage's —
          we'll support full SES domain identity verification. You'll add 3 DKIM CNAME records
          to your domain's DNS, and SES signs all outgoing mail with your domain key. DMARC
          alignment becomes proper. Email-level verification works today and is good enough for
          most volumes.
        </p>
      </Sub>
    </SectionContainer>
  );
}

/* ── Categories ───────────────────────────────────────────────────────── */

function CategoriesHelp() {
  return (
    <SectionContainer id="categories" title="Setting up categories" icon={Sparkles}>
      <p>
        Categories (a.k.a. intents) are how Triage classifies inbound email. Each category has
        a name, description, examples, optional matching rules, optional slots, and reply
        behavior settings.
      </p>
      <Sub title="Description matters most">
        <p>
          The classifier reads your description as the primary signal. Be specific.
        </p>
        <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
          <div className="rounded-md border border-rose-200 bg-rose-50/50 p-3 text-sm">
            <p className="text-xs font-medium uppercase tracking-wide text-rose-700">Vague</p>
            <p className="mt-1 text-rose-900">"Order issues"</p>
          </div>
          <div className="rounded-md border border-emerald-200 bg-emerald-50/50 p-3 text-sm">
            <p className="text-xs font-medium uppercase tracking-wide text-emerald-700">Specific</p>
            <p className="mt-1 text-emerald-900">
              "Customer is asking about the delivery status of an order they've already placed —
              not a refund, not a cancellation, not a new purchase."
            </p>
          </div>
        </div>
      </Sub>
      <Sub title="Examples are the fuel">
        <ul className="list-inside list-disc space-y-1.5">
          <li>Minimum 3, ideal 5–8.</li>
          <li>Use real (anonymized) customer phrasings, not made-up ones.</li>
          <li>Cover the obvious case AND a few edge cases.</li>
          <li>Don't make examples too long — a one-line subject and 1–2 sentence snippet is plenty.</li>
        </ul>
      </Sub>
      <Sub title="Matching rules — optional but powerful">
        <p>
          When the email's category is obvious from a keyword or pattern, deterministic rules
          skip the LLM (saves cost + latency). Examples:
        </p>
        <ul className="mt-2 list-inside list-disc space-y-1">
          <li><code className="rounded bg-gray-100 px-1 text-xs">subject_contains: "refund"</code> → maps directly to Refund category.</li>
          <li><code className="rounded bg-gray-100 px-1 text-xs">body_regex: "\bMHL-\\d{6}\\b"</code> → order ID present, route to Report status.</li>
          <li><code className="rounded bg-gray-100 px-1 text-xs">from_domain_in: ["partner1.com", "partner2.com"]</code> → known partners.</li>
        </ul>
        <p className="mt-2 text-sm text-gray-600">
          Rules within an intent are AND-ed; across intents, first priority match wins. Keep them
          tight — a rule that matches too broadly fights with the LLM's better judgment.
        </p>
      </Sub>
    </SectionContainer>
  );
}

/* ── Knowledge ────────────────────────────────────────────────────────── */

function KnowledgeHelp() {
  return (
    <SectionContainer id="knowledge" title="Writing a good knowledge bank" icon={BookOpen}>
      <p>
        Each category has a 5,000-character knowledge field. The reply LLM treats it as
        authoritative — it won't contradict the content and will offer to escalate when
        something isn't covered.
      </p>
      <Sub title="What works well">
        <ul className="list-inside list-disc space-y-1.5">
          <li><strong>Bullet points beat paragraphs.</strong> The LLM extracts facts more reliably from concise, structured content.</li>
          <li><strong>Lead with answers.</strong> "Refund window: 30 days from purchase" — not "Our refund policy is structured to provide flexibility…".</li>
          <li><strong>Include exact numbers.</strong> Dates, prices, plan limits, support hours.</li>
          <li><strong>Cover the top 5 questions per category.</strong> The 5k limit forces prioritization.</li>
        </ul>
      </Sub>
      <Sub title="What doesn't">
        <ul className="list-inside list-disc space-y-1.5">
          <li>Marketing copy. Strip the fluff.</li>
          <li>Internal jargon the customer won't understand.</li>
          <li>"See KB article 1234" without saying what 1234 contains. The LLM can't follow links.</li>
          <li>Conflicting information. The LLM may pick the wrong one.</li>
        </ul>
      </Sub>
      <Sub title="Example structure">
        <pre className="overflow-x-auto rounded-md border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-xs leading-relaxed">
          {`Refund policy:
- Full refund within 30 days, no questions asked.
- 50% refund days 31–60.
- No refund after 60 days.

Plan limits:
- Starter: 1,000 emails/mo, $19.
- Growth: 10,000 emails/mo, $99.
- Enterprise: contact sales.

Account questions:
- Reset link expires after 1 hour.
- 2FA setup: settings → security → enable.
- Account deletion: 30-day hold, then permanent.

Out-of-scope: I'll escalate.`}
        </pre>
      </Sub>
    </SectionContainer>
  );
}

/* ── Reply modes ──────────────────────────────────────────────────────── */

function ReplyModesHelp() {
  return (
    <SectionContainer id="reply-modes" title="Auto-replies vs review mode" icon={Sliders}>
      <p>
        Triage has four switches that gate auto-send. <strong>All four must be true</strong> for
        a reply to send autonomously:
      </p>
      <ol className="mt-3 list-inside list-decimal space-y-2 text-sm">
        <li>
          <strong>Tenant Auto-replies = ON</strong> — the global toggle (Settings or footer).
        </li>
        <li>
          <strong>Tenant Dry-run = OFF</strong> — when ON, even approved replies aren't actually
          sent. Useful for testing.
        </li>
        <li>
          <strong>Category reply mode = Direct</strong> — per-category. Default is Draft (admin
          reviews). Switch high-confidence categories to Direct.
        </li>
        <li>
          <strong>Case confidence ≥ category auto-send threshold</strong> — defaults to 0.75.
          Higher = more conservative.
        </li>
      </ol>
      <p className="mt-4 text-sm text-gray-600">
        If any one of these fails, the reply queues for admin approval in the{" "}
        <Link to="/review" className="font-medium text-gray-900 underline">Review queue</Link>.
        The case detail page tells you exactly which condition blocked auto-send.
      </p>
      <Sub title="Recommended starting policy">
        <ul className="list-inside list-disc space-y-1.5 text-sm">
          <li><strong>Day 1:</strong> Dry-run ON, Auto-replies ON. Drafts queue, nothing sends — practice mode.</li>
          <li><strong>After ~20 reviewed drafts:</strong> Dry-run OFF for low-risk categories (password reset, generic FAQ).</li>
          <li><strong>After ~100 reviewed drafts per category:</strong> flip individual categories to Direct mode.</li>
          <li><strong>Always keep Direct mode OFF</strong> for billing, refund, and account-deletion categories.</li>
        </ul>
      </Sub>
    </SectionContainer>
  );
}

/* ── Models ───────────────────────────────────────────────────────────── */

function BusinessContextHelp() {
  return (
    <SectionContainer id="business-context" title="Business context" icon={Building2}>
      <p>
        Free-text background about your business — products, brand voice, key policies, hours.
        Configure in <Link to="/settings" className="font-medium text-gray-900 underline">Settings → Business context</Link>.
      </p>
      <p className="mt-2">
        Triage injects this into the system prompt of every reply (not classification, not
        gather). It's the highest-level grounding: applied regardless of which category an email
        falls into, before per-category knowledge banks add specifics.
      </p>

      <Sub title="What to put in there">
        <ul className="list-inside list-disc space-y-1 text-sm">
          <li>One-line description of what your business does and who your customers are.</li>
          <li>Brand voice — formal vs. casual, brief vs. warm, any words to favor or avoid.</li>
          <li>Cross-cutting policies that apply broadly: refund window, support hours, escalation criteria.</li>
          <li>Key facts you want the AI to never invent — "we ship within India only", "we're closed on Sundays".</li>
        </ul>
      </Sub>

      <Sub title="What NOT to put in there">
        <ul className="list-inside list-disc space-y-1 text-sm">
          <li>Per-category FAQ-style content — use the category's <strong>Knowledge bank</strong> for that. Knowledge banks are more granular and don't bloat every reply prompt.</li>
          <li>Customer-specific data — fetched live via Connectors at reply time.</li>
          <li>Long marketing copy — keep it under 1,500 chars even though the hard cap is 3,000.</li>
        </ul>
      </Sub>

      <Sub title="Three layers of grounding, from broad to specific">
        <ul className="list-inside list-decimal space-y-1 text-sm">
          <li><strong>Business context</strong> — every reply, every category</li>
          <li><strong>Category knowledge bank</strong> — only when an email matches that category</li>
          <li><strong>Live data from Connectors</strong> — fetched per email, real-time facts</li>
        </ul>
        <p className="mt-2 text-xs text-gray-500">
          The reply prompt presents these in order; the AI is told to treat each as authoritative
          and never invent values outside them.
        </p>
      </Sub>
    </SectionContainer>
  );
}

/* ── Sender filter ────────────────────────────────────────────────────── */

function SenderFilterHelp() {
  return (
    <SectionContainer id="sender-filter" title="Sender filter" icon={ShieldOff}>
      <p>
        A drop-list for unwanted inbound. Mail whose <code className="rounded bg-gray-100 px-1 text-xs">From:</code> matches an entry is silently dropped
        before classification — no case created, no quota consumed. Configure in{" "}
        <Link to="/settings" className="font-medium text-gray-900 underline">Settings → Guardrails → Ignore senders</Link>.
      </p>

      <Sub title="What you can add">
        <ul className="list-inside list-disc space-y-1 text-sm">
          <li><strong>A specific email</strong>: <code className="rounded bg-gray-100 px-1 text-xs">noreply@vendor.com</code> — exact match, case-insensitive.</li>
          <li><strong>A bare domain</strong>: <code className="rounded bg-gray-100 px-1 text-xs">vendor.com</code> — matches any address ending in <code className="rounded bg-gray-100 px-1 text-xs">@vendor.com</code> or its subdomains (e.g. <code className="rounded bg-gray-100 px-1 text-xs">mail.vendor.com</code>).</li>
        </ul>
      </Sub>

      <Sub title="Common use cases">
        <ul className="list-inside list-disc space-y-1 text-sm">
          <li>Your team members forwarding internal threads into the support inbox — add your own company domain.</li>
          <li>Marketing automation (Mailchimp, HubSpot bounces, Stripe receipts) accidentally hitting support.</li>
          <li>One-off spammers you can't quite shake.</li>
        </ul>
      </Sub>

      <Sub title="What it matches against">
        <p>
          The parsed <code className="rounded bg-gray-100 px-1 text-xs">From:</code> header of the email — <em>not</em> the SMTP envelope. So when Gmail
          auto-forwards a customer's email through to Triage, the original customer's email is
          still what gets matched. You won't accidentally filter out legitimate forwarded mail
          just because the envelope sender is your Gmail account.
        </p>
      </Sub>

      <Sub title="Where to find what got dropped">
        <p>
          Every dropped email writes an <code className="rounded bg-gray-100 px-1 text-xs">email.ignored</code> entry to the audit log with the matched
          pattern and the original sender — see <a href="#audit-log" className="font-medium text-gray-900 underline">Audit log</a> below.
        </p>
      </Sub>
    </SectionContainer>
  );
}

/* ── Outbound archive ─────────────────────────────────────────────────── */

function OutboundArchiveHelp() {
  return (
    <SectionContainer id="outbound-archive" title="Outbound archive" icon={Archive}>
      <p>
        BCCs every outbound reply (and clarification email) to an address you control, so you
        have a record in your own mailbox — even if you stop using Triage. Configure in{" "}
        <Link to="/settings" className="font-medium text-gray-900 underline">Settings → Archive outbound to my inbox</Link>.
      </p>

      <Sub title="What it does">
        <ul className="list-inside list-disc space-y-1 text-sm">
          <li>Every AI-sent reply gets a hidden <code className="rounded bg-gray-100 px-1 text-xs">Bcc:</code> to your configured address. The customer never sees it.</li>
          <li>Auto-populates to your verified <strong>sender email</strong> when you first verify one. You can change or clear it any time.</li>
          <li>Gmail filter-friendly — search <code className="rounded bg-gray-100 px-1 text-xs">from:support@yourcompany.com</code> to see everything Triage has sent on your behalf.</li>
        </ul>
      </Sub>

      <Sub title="Loop safety">
        <p>
          If the archive address itself forwards mail back to Triage (e.g. your Gmail has an
          auto-forwarder to the inbound subdomain), Triage detects the loop via a stamped header
          and drops the BCC'd copy before any AI runs. Three independent defenses — header match,
          From == your sender email, From == your archive email — so loops can't sneak through.
        </p>
      </Sub>

      <Sub title="When to leave it off">
        <p>
          Mostly when you don't want your own inbox flooded — but the BCC also won't appear in
          your "Sent" folder, only your Inbox. Set up a Gmail filter to auto-label and archive
          them if the volume is high.
        </p>
      </Sub>
    </SectionContainer>
  );
}

/* ── Connectors ───────────────────────────────────────────────────────── */

function ConnectorsHelp() {
  return (
    <SectionContainer id="connectors" title="Connectors" icon={Plug}>
      <p>
        HTTPS endpoints the AI calls before drafting a reply, so it can answer with real-time
        facts from your other systems (order status, account state, license tier, …). Define
        them in <Link to="/connectors" className="font-medium text-gray-900 underline">Connectors</Link>{" "}
        and link a connector to any category from the category's detail page.
      </p>

      <Sub title="The fetch step in the workflow">
        <p>
          Once an email is classified into a category and any required slots are filled (via
          extraction or gather), Triage runs the linked connector and injects the response
          fields into the reply prompt as authoritative <em>Live data</em>. The AI is told to
          treat those values exactly and never invent them.
        </p>
        <p className="mt-2 text-sm text-gray-600">
          Connectors are <strong>read-only</strong> in v1 — GET and POST (for lookup-style APIs)
          only. Writes / updates come in a later phase.
        </p>
      </Sub>

      <Sub title="Anatomy of a connector">
        <ul className="list-inside list-disc space-y-1 text-sm">
          <li><strong>Method + URL</strong>: GET or POST, HTTPS only. Path supports templates.</li>
          <li><strong>Query params + headers</strong>: key/value pairs, values can be templates.</li>
          <li><strong>Body</strong> (POST only): JSON template.</li>
          <li><strong>Variables</strong>: connector-level constants like <code className="rounded bg-gray-100 px-1 text-xs">shop_domain</code> referenced via <code className="rounded bg-gray-100 px-1 text-xs">{`{{vars.shop_domain}}`}</code>.</li>
          <li><strong>Authentication</strong>: API-key in a header. Choose the header name + an optional value prefix like <code className="rounded bg-gray-100 px-1 text-xs">Bearer </code>. Secret is stored encrypted via AWS KMS with per-tenant context.</li>
          <li><strong>Response mapping</strong>: dot-paths into the JSON response, named for the AI. Example: <code className="rounded bg-gray-100 px-1 text-xs">status: data.order.financial_status</code>.</li>
          <li><strong>Timeout</strong>: default 10s, max 30s.</li>
        </ul>
      </Sub>

      <Sub title="Template tokens">
        <p>Use these anywhere — path, query values, header values, body:</p>
        <ul className="list-inside list-disc space-y-1 text-sm">
          <li><code className="rounded bg-gray-100 px-1 text-xs">{`{{slots.<name>}}`}</code> — slot values from the case (extracted or gathered)</li>
          <li><code className="rounded bg-gray-100 px-1 text-xs">{`{{vars.<name>}}`}</code> — connector-level constant</li>
          <li><code className="rounded bg-gray-100 px-1 text-xs">{`{{customer.email}}`}</code> / <code className="rounded bg-gray-100 px-1 text-xs">{`{{customer.name}}`}</code></li>
          <li><code className="rounded bg-gray-100 px-1 text-xs">{`{{case.id}}`}</code> / <code className="rounded bg-gray-100 px-1 text-xs">{`{{case.subject}}`}</code></li>
        </ul>
        <p className="mt-2 text-sm text-gray-600">
          Substitution is context-aware — URL segments are percent-encoded, JSON body values are
          properly escaped, header values get CR/LF stripped (header-injection prevention).
        </p>
      </Sub>

      <Sub title="Test before you link">
        <p>
          After saving a connector with a secret, the right panel shows a <strong>Test</strong>{" "}
          form: type sample slot values and customer.email, hit Run. You see the exact request
          that goes out (auth header redacted) plus the raw response and the mapped output.
          Fix templates and mappings here before linking to a category.
        </p>
      </Sub>

      <Sub title="Safety rails">
        <ul className="list-inside list-disc space-y-1 text-sm">
          <li><strong>HTTPS only</strong>. Plain HTTP rejected at save and runtime.</li>
          <li><strong>SSRF blocking</strong>: private IPs (RFC 1918), loopback, link-local (incl. AWS instance-metadata at 169.254.169.254), IPv6 ULA — all blocked.</li>
          <li><strong>100KB response cap</strong> — larger responses fail with <code className="rounded bg-gray-100 px-1 text-xs">response_too_large</code> rather than being processed.</li>
          <li><strong>Graceful degrade</strong>: any failure (template error, network, 4xx/5xx, bad JSON) skips the live-data block and proceeds to reply with whatever info Triage already has. An entry lands in the audit log.</li>
        </ul>
      </Sub>

      <Sub title="When to use a connector">
        <ul className="list-inside list-disc space-y-1 text-sm">
          <li><strong>Order status / shipment tracking</strong> for e-commerce.</li>
          <li><strong>Account / subscription state</strong> for SaaS.</li>
          <li><strong>Appointment lookup</strong> for service businesses.</li>
          <li><strong>License key validation</strong>, <strong>course enrollment status</strong>, etc.</li>
        </ul>
      </Sub>
    </SectionContainer>
  );
}

/* ── Slack digest ─────────────────────────────────────────────────────── */

function SlackDigestHelp() {
  return (
    <SectionContainer id="slack-digest" title="Slack digest" icon={Bell}>
      <p>
        A daily one-message summary of inbox activity posted to a Slack channel — so you know
        Triage is working without checking the dashboard. Configure in{" "}
        <Link to="/settings" className="font-medium text-gray-900 underline">Settings → Daily Slack digest</Link>.
      </p>

      <Sub title="Setting it up">
        <ul className="list-inside list-decimal space-y-1 text-sm">
          <li>
            In Slack, create an Incoming Webhook for the channel you want digests in. Go to{" "}
            <code className="rounded bg-gray-100 px-1 text-xs">api.slack.com/apps</code> → your
            app → Incoming Webhooks → Add New Webhook to Workspace.
          </li>
          <li>Copy the webhook URL — it looks like <code className="rounded bg-gray-100 px-1 text-xs">https://hooks.slack.com/services/T0…/B0…/…</code>.</li>
          <li>Paste it into Settings → Daily Slack digest → Incoming webhook URL.</li>
          <li>Pick the local hour you want the digest to land and your timezone.</li>
          <li>Click <strong>Send test now</strong> to verify before enabling.</li>
          <li>Toggle <strong>Enable daily digest</strong> on, save.</li>
        </ul>
      </Sub>

      <Sub title="What's in the digest">
        <ul className="list-inside list-disc space-y-1 text-sm">
          <li><strong>24-hour rolling window</strong>: received, auto-replied (with %), queued for review, escalated, awaiting customer info, below threshold.</li>
          <li><strong>Top 3 categories</strong> by volume.</li>
          <li><strong>Up to 3 recent escalations</strong> with subject + customer email.</li>
          <li><strong>Link to the dashboard</strong> for one-click drill-in.</li>
        </ul>
      </Sub>

      <Sub title="Timing gotcha">
        <p>
          The cron fires at the top of every UTC hour. Timezones with 30- or 45-minute offsets
          (India, Iran, Nepal, parts of Australia) receive the digest at <strong>HH:30 local</strong>{" "}
          instead of HH:00. So setting "9:00 AM Asia/Kolkata" actually delivers at 9:30 AM local.
          Acceptable for v1 — give the time some slack in your expectations.
        </p>
      </Sub>
    </SectionContainer>
  );
}

/* ── Audit log ────────────────────────────────────────────────────────── */

function AuditLogHelp() {
  return (
    <SectionContainer id="audit-log" title="Audit log" icon={ScrollText}>
      <p>
        Every system event Triage records — categorization, slot extraction, sends, drops,
        connector calls — surfaced for the last <strong>30 days</strong>. Open via{" "}
        <Link to="/audit" className="font-medium text-gray-900 underline">Audit</Link>{" "}
        in the nav.
      </p>

      <Sub title="What you can filter on">
        <ul className="list-inside list-disc space-y-1 text-sm">
          <li><strong>Event type</strong>: 14 known types in a dropdown — email received, classification matched, slot extracted, reply sent, escalations, etc.</li>
          <li><strong>Date range</strong>: last 24h / 7d / 30d / all (30-day TTL).</li>
          <li><strong>Case ID</strong>: drill into the full timeline for a specific case.</li>
        </ul>
      </Sub>

      <Sub title="Useful event types when debugging">
        <ul className="list-inside list-disc space-y-1 text-sm">
          <li><code className="rounded bg-gray-100 px-1 text-xs">email.ignored</code> — sender filter or self-loop dropped an inbound. Payload includes the matched pattern.</li>
          <li><code className="rounded bg-gray-100 px-1 text-xs">email.rejected</code> — quota / suspended / trial-expired blocked an inbound.</li>
          <li><code className="rounded bg-gray-100 px-1 text-xs">classification.parse_failed</code> — Bedrock returned something unparseable.</li>
          <li><code className="rounded bg-gray-100 px-1 text-xs">slot.validation_failed</code> — extracted value didn't pass the validation regex.</li>
          <li><code className="rounded bg-gray-100 px-1 text-xs">connector.invoked</code> / <code className="rounded bg-gray-100 px-1 text-xs">connector.failed</code> — every Connector run.</li>
          <li><code className="rounded bg-gray-100 px-1 text-xs">reply.send_failed</code> — outbound SES error.</li>
        </ul>
      </Sub>

      <Sub title="Privacy">
        <p>
          Free-text emails and phone numbers in audit payloads are scrubbed at write time — so
          rummaging through the log won't expose customer PII more than necessary. The customer's
          From-address on the case header is preserved for routing context, but quoted message
          bodies and slot values that look like PII are tagged <code className="rounded bg-gray-100 px-1 text-xs">[REDACTED]</code>.
        </p>
      </Sub>

      <Sub title="Retention beyond 30 days">
        <p>
          The 30-day window is the hot lookup. Long-term retention writes to S3 via DynamoDB
          Streams + Firehose, partitioned by tenant + date. If you need to retrieve historical
          events beyond the UI, email{" "}
          <a href="mailto:hello@appgambit.com" className="font-medium text-gray-900 underline">hello@appgambit.com</a>{" "}
          with the date range.
        </p>
      </Sub>
    </SectionContainer>
  );
}

/* ── Troubleshooting ──────────────────────────────────────────────────── */

function Troubleshooting() {
  const issues: Array<{ q: string; a: ReactNode }> = [
    {
      q: "Test email never arrives",
      a: (
        <>
          <ol className="list-inside list-decimal space-y-1">
            <li><strong>Check the banner at the top of any page</strong>. If your account is suspended, your trial has expired, or your email quota is exhausted, an amber/red banner says so and inbound is paused.</li>
            <li>Check the <Link to="/audit" className="font-medium text-gray-900 underline">Audit log</Link> for <code className="rounded bg-gray-100 px-1 text-xs">email.ignored</code> or <code className="rounded bg-gray-100 px-1 text-xs">email.rejected</code> events — the payload says which rule fired (sender filter, self-loop, quota, etc.).</li>
            <li>If you set up forwarding, verify it's active by sending directly to the inbound subdomain (skip the forward).</li>
            <li>Confirm your DNS is pointing the right way — both forwarder and (if applicable) MX records.</li>
            <li>If sandboxed in SES outbound, sender-side bounces can affect inbound auditing — check your SES suppression list.</li>
          </ol>
        </>
      ),
    },
    {
      q: "Email arrives but doesn't classify",
      a: (
        <ol className="list-inside list-decimal space-y-1">
          <li>Open <Link to="/categories" className="font-medium text-gray-900 underline">Categories</Link> — at least one must be Active.</li>
          <li>Open the case → click <strong>Reclassify</strong>. The error (if any) shows the actual cause.</li>
          <li>Check the <Link to="/audit" className="font-medium text-gray-900 underline">Audit log</Link> for <code className="rounded bg-gray-100 px-1 text-xs">classification.parse_failed</code> events — the payload shows the model's raw output.</li>
          <li>If classification consistently fails, the model your tenant is using may not have access enabled. Contact <a href="mailto:hello@appgambit.com" className="font-medium text-gray-900 underline">hello@appgambit.com</a> — model selection is managed by Triage support, not in your settings.</li>
        </ol>
      ),
    },
    {
      q: "AI draft generates but never sends (auto-replies on)",
      a: (
        <>
          <p>The case detail page's "AI workflow" panel will name the specific blocker. Most often:</p>
          <ul className="mt-2 list-inside list-disc space-y-1">
            <li><strong>Dry-run mode is ON</strong> — toggle off in Settings or the footer.</li>
            <li><strong>Category reply mode is Draft</strong> — open the category and switch Reply behavior to Direct.</li>
            <li><strong>Confidence below auto-send threshold</strong> — lower the threshold or improve examples.</li>
            <li><strong>Max replies per case reached</strong> — guardrail kicked in. Approve manually.</li>
          </ul>
        </>
      ),
    },
    {
      q: "Reply lands in spam",
      a: (
        <ol className="list-inside list-decimal space-y-1">
          <li>Add SPF to your sender domain: <code className="rounded bg-gray-100 px-1 text-xs">v=spf1 include:amazonses.com -all</code> (merge if you already have one).</li>
          <li>If you have strict DMARC (<code className="rounded bg-gray-100 px-1 text-xs">p=reject</code>), relax to <code className="rounded bg-gray-100 px-1 text-xs">p=quarantine; aspf=r</code> until v1.1 ships full domain DKIM verification.</li>
          <li>Get out of SES sandbox — you'll get auto-bounced by some providers when sandboxed.</li>
          <li>Warm up the domain — send a few dozen emails to internal addresses before going to volume.</li>
        </ol>
      ),
    },
    {
      q: "Customer reply creates a new case instead of threading",
      a: (
        <>
          <p>
            Threading depends on the customer's mail client preserving <code className="rounded bg-gray-100 px-1 text-xs">In-Reply-To</code>{" "}
            and <code className="rounded bg-gray-100 px-1 text-xs">References</code> headers. Almost all do. If yours doesn't:
          </p>
          <ul className="mt-2 list-inside list-disc space-y-1">
            <li>Some forwarders strip headers. Use a "Bcc" rule or plain forward instead of a redirect.</li>
            <li>If a customer copies+pastes a fresh email instead of hitting reply, headers won't match.</li>
            <li>In Triage subdomain mode, the plus-address Reply-To gives a backup threading channel.</li>
          </ul>
        </>
      ),
    },
    {
      q: "Sender email verification stays Pending forever",
      a: (
        <ul className="list-inside list-disc space-y-1">
          <li>Click <em>Resend</em> in Settings — first verification emails sometimes get filtered.</li>
          <li>Check spam, promotions, and "All mail" — the email is from <code className="rounded bg-gray-100 px-1 text-xs">no-reply-aws@amazon.com</code>.</li>
          <li>Click <em>Check</em> after clicking the link. The page polls every 5s but a manual check forces a refresh.</li>
          <li>If the address is wrong, click <em>Remove</em> and re-add.</li>
        </ul>
      ),
    },
  ];

  return (
    <SectionContainer id="troubleshooting" title="Troubleshooting" icon={AlertTriangle}>
      <div className="space-y-3">
        {issues.map((i, idx) => (
          <details
            key={idx}
            className="group rounded-md border border-gray-200 bg-white p-4 open:bg-gray-50"
          >
            <summary className="flex cursor-pointer items-center justify-between gap-3 text-sm font-medium text-gray-900">
              <span>{i.q}</span>
              <ChevronRight className="h-4 w-4 shrink-0 text-gray-400 transition-transform group-open:rotate-90" />
            </summary>
            <div className="mt-3 text-sm text-gray-600">{i.a}</div>
          </details>
        ))}
      </div>

      <p className="mt-8 text-sm text-gray-500">
        Still stuck? Email{" "}
        <a href="mailto:hello@appgambit.com" className="font-medium text-gray-900 underline">
          hello@appgambit.com
        </a>{" "}
        with your tenant slug and a description of the issue.
      </p>
    </SectionContainer>
  );
}

/* ── building blocks ──────────────────────────────────────────────────── */

function SectionContainer({
  id,
  title,
  icon: Icon,
  children,
}: {
  id: string;
  title: string;
  icon: typeof Mail;
  children: ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-20">
      <h2 className="flex items-center gap-2 text-2xl font-semibold tracking-tight text-gray-900">
        <Icon className="h-5 w-5 text-gray-400" />
        {title}
      </h2>
      <div className="prose-sm mt-4 space-y-2 text-sm leading-relaxed text-gray-700">{children}</div>
    </section>
  );
}

function Sub({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mt-6">
      <h3 className="text-base font-semibold text-gray-900">{title}</h3>
      <div className="mt-2 space-y-2 text-sm text-gray-700">{children}</div>
    </div>
  );
}

function Provider({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card className="mt-3">
      <CardHeader title={title} />
      <CardBody className="text-sm text-gray-700">{children}</CardBody>
    </Card>
  );
}

function CopyChip({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <span className="inline-flex items-center gap-1 align-middle">
      <code className="rounded bg-gray-100 px-1 text-xs">{text}</code>
      <Button size="sm" variant="ghost" onClick={copy}>
        <Copy className="h-3 w-3" />
        <span className="text-xs">{copied ? "Copied" : "Copy"}</span>
      </Button>
    </span>
  );
}
